import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { useTheme } from "../context/ThemeContext";
import logviComplete from "../assets/icons/logvi-complete.png";

/** --- Resolver de avatar para CRA/Webpack (require.context) --- */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

/** ======= Cotações: config ======= */
const FX_REFRESH_MS = 5 * 60 * 1000;
const FX_TIMEOUT_MS = 7000;

type FxTriple = { USD: number | null; EUR: number | null; GBP: number | null };
type FxResult = FxTriple & { at: number | null; from: number | null };

function hashIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return h % 6;
}

export function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];
    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1;
  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];
  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  for (let i = 1; i <= 6; i++) {
    try {
      return profileCtx(`./79372ad3df5e70d16bc4b1369300763f.jpg`) as string;
    } catch {}
  }
  return undefined;
}

export function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase();
}

function median(nums: number[]): number | null {
  const arr = nums.filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!arr.length) return null;
  const mid = Math.floor(arr.length / 2);
  return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
}

function pad2(n: number) {
  return n.toString().padStart(2, "0");
}

async function fxFromHost(signal?: AbortSignal): Promise<FxTriple> {
  const url =
    "https://api.exchangerate.host/latest?base=BRL&symbols=USD,EUR,GBP";
  const r = await fetch(url, { signal, cache: "no-store" });
  const j = await r.json();
  const inv = (x: any) => (Number(x) ? 1 / Number(x) : null);
  return {
    USD: inv(j?.rates?.USD),
    EUR: inv(j?.rates?.EUR),
    GBP: inv(j?.rates?.GBP),
  };
}

async function fxFromER(signal?: AbortSignal): Promise<FxTriple> {
  const url = "https://open.er-api.com/v6/latest/BRL";
  const r = await fetch(url, { signal, cache: "no-store" });
  const j = await r.json();
  const inv = (x: any) => (Number(x) ? 1 / Number(x) : null);
  return {
    USD: inv(j?.rates?.USD),
    EUR: inv(j?.rates?.EUR),
    GBP: inv(j?.rates?.GBP),
  };
}

async function fxFromAwesome(signal?: AbortSignal): Promise<FxTriple> {
  const url = "https://economia.awesomeapi.com.br/last/USD-BRL,EUR-BRL,GBP-BRL";
  const r = await fetch(url, { signal, cache: "no-store" });
  const j = await r.json();
  const take = (k: string) => {
    const v = j?.[k]?.ask ?? j?.[k]?.bid;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    USD: take("USDBRL"),
    EUR: take("EURBRL"),
    GBP: take("GBPBRL"),
  };
}

function mergeFxTriples(list: FxTriple[]): FxTriple {
  const USD = median(
    list.map((x) => x.USD!).filter((v) => v != null) as number[]
  );
  const EUR = median(
    list.map((x) => x.EUR!).filter((v) => v != null) as number[]
  );
  const GBP = median(
    list.map((x) => x.GBP!).filter((v) => v != null) as number[]
  );
  return { USD, EUR, GBP };
}

function fmtBRL(n: number | null) {
  if (!Number.isFinite(Number(n))) return "—";
  return Number(n!).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

const RateTickerInline: React.FC<{ netOnline: boolean }> = ({ netOnline }) => {
  const [fx, setFx] = useState<FxResult>({
    USD: null,
    EUR: null,
    GBP: null,
    at: null,
    from: null,
  });
  const [loading, setLoading] = useState(true);

  const loadFx = useCallback(async () => {
    if (!netOnline) return;
    setLoading(true);
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), FX_TIMEOUT_MS);
    try {
      const results = await Promise.allSettled([
        fxFromHost(ac.signal),
        fxFromER(ac.signal),
        fxFromAwesome(ac.signal),
      ]);
      const ok: FxTriple[] = [];
      for (const r of results) {
        if (r.status === "fulfilled" && r.value) ok.push(r.value);
      }
      if (!ok.length) return;
      const merged = mergeFxTriples(ok);
      setFx((old) => ({
        USD: merged.USD ?? old.USD,
        EUR: merged.EUR ?? old.EUR,
        GBP: merged.GBP ?? old.GBP,
        from: old.at ?? null,
        at: Date.now(),
      }));
    } catch {
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, [netOnline]);

  useEffect(() => {
    loadFx();
    if (!netOnline) return;
    const id = setInterval(loadFx, FX_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadFx, netOnline]);

  const updatedLabel = useMemo(() => {
    if (!fx.at) return "—";
    const d = new Date(fx.at);
    return `Atualizado às ${d.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  }, [fx.at]);

  const baseText: React.CSSProperties = {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 300,
    color: "#FFFFFF",
    opacity: 0.88,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const sep: React.CSSProperties = { opacity: 0.45, padding: "0 6px" };
  const sym: React.CSSProperties = { opacity: 0.9, paddingRight: 4 };

  const usd = loading ? "—" : fmtBRL(fx.USD);
  const eur = loading ? "—" : fmtBRL(fx.EUR);
  const gbp = loading ? "—" : fmtBRL(fx.GBP);

  return (
    <div
      style={{ marginRight: "auto", paddingLeft: 12, display: "flex" }}
      title={updatedLabel}
      aria-label={`Cotações • ${updatedLabel}`}
    >
      <div style={baseText}>
        <span style={{ fontWeight: 400 }}>USD</span> <span style={sym}>R$</span>
        <span>{usd.replace("R$ ", "").replace("R$", "")}</span>
        <span style={sep}>·</span>
        <span style={{ fontWeight: 400 }}>EUR</span> <span style={sym}>R$</span>
        <span>{eur.replace("R$ ", "").replace("R$", "")}</span>
        <span style={sep}>·</span>
        <span style={{ fontWeight: 400 }}>GBP</span> <span style={sym}>R$</span>
        <span>{gbp.replace("R$ ", "").replace("R$", "")}</span>
      </div>
    </div>
  );
};

type BRDate = { month: number; day: number; name: string; emoji?: string };

const BR_COMM_DATES: BRDate[] = [
  { month: 1, day: 1, name: "Confraternização Universal 🎉" },
];

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function getNextBRCommemorative(
  fromDate = new Date()
): { date: Date; label: string } | null {
  const y = fromDate.getFullYear();
  const pool: { d: Date; label: string }[] = [];

  for (const item of BR_COMM_DATES) {
    const dThis = new Date(y, item.month - 1, item.day, 12, 0, 0);
    const dNext = new Date(y + 1, item.month - 1, item.day, 12, 0, 0);

    if (dThis >= startOfDay(fromDate)) {
      pool.push({ d: dThis, label: item.name });
    } else {
      pool.push({ d: dNext, label: item.name });
    }
  }

  pool.sort((a, b) => a.d.getTime() - b.d.getTime());
  return pool.length ? { date: pool[0].d, label: pool[0].label } : null;
}

function getMonthAwarenessColor(monthIndex0: number): {
  name: string;
  hex: string;
} {
  const map: Record<number, { name: string; hex: string }> = {
    0: { name: "Janeiro Branco", hex: "#E5E7EB" },
    1: { name: "Fevereiro Roxo", hex: "#7C3AED" },
    2: { name: "Março Lilás", hex: "#C084FC" },
    3: { name: "Abril Azul", hex: "#2563EB" },
    4: { name: "Maio Amarelo", hex: "#F59E0B" },
    5: { name: "Junho Vermelho", hex: "#DC2626" },
    6: { name: "Julho Amarelo", hex: "#FBBF24" },
    7: { name: "Agosto Dourado", hex: "#F59E0B" },
    8: { name: "Setembro Amarelo", hex: "#FDE047" },
    9: { name: "Outubro Rosa", hex: "#F472B6" },
    10: { name: "Novembro Azul", hex: "#3B82F6" },
    11: { name: "Dezembro Vermelho", hex: "#EF4444" },
  };
  return map[monthIndex0] ?? { name: "—", hex: "#9CA3AF" };
}

const HRInline: React.FC = () => {
  const now = new Date();
  const next = useMemo(() => getNextBRCommemorative(now), [now]);
  const monthInfo = useMemo(
    () => getMonthAwarenessColor(now.getMonth()),
    [now]
  );

  const baseText: React.CSSProperties = {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 300,
    color: "#FFFFFF",
    opacity: 0.9,
    whiteSpace: "nowrap",
    userSelect: "none",
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginRight: "auto",
    paddingLeft: 12,
  };

  const chip: React.CSSProperties = {
    width: 10,
    height: 10,
    borderRadius: 999,
    boxShadow: "0 0 0 2px rgba(255,255,255,0.15)",
    backgroundColor: monthInfo.hex,
    display: "inline-block",
  };

  const dateText = next
    ? `${pad2(next.date.getDate())}/${pad2(next.date.getMonth() + 1)} — ${
        next.label
      }`
    : "—";

  return (
    <div style={baseText} aria-label="Próxima data comemorativa e cor do mês">
      <span>
        <strong>Próxima data:</strong> {dateText}
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={chip} aria-hidden />
        <strong>Cor do mês:</strong> {monthInfo.name}
      </span>
    </div>
  );
};

const Header: React.FC = () => {
  const { sessao, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const nome = sessao?.nome || "";
  const departamento = sessao?.departamento || "";
  const empresaRazaoSocial = sessao?.empresa_razao_social || "ERRO INTERNO";

  const empresaCnpj = sessao?.empresa_cnpj || "12345678910";
  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [loggingOut, setLoggingOut] = useState<boolean>(false);
  const [liveUrl, setLiveUrl] = useState<string | undefined>(undefined);
  const [companyMenuOpen, setCompanyMenuOpen] = useState<boolean>(false);
  const [changingCompanySlug, setChangingCompanySlug] = useState<string | null>(
    null
  );
  const [userAreaHovered, setUserAreaHovered] = useState<boolean>(false);

  const { theme, systemTitle } = useTheme();
  const menuRef = useRef<HTMLDivElement | null>(null);

  const [netOnline, setNetOnline] = useState<boolean>(navigator.onLine);
  const [apiReachable, setApiReachable] = useState<boolean | null>(null);
  const [lastPingAt, setLastPingAt] = useState<number | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!menuOpen) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }

    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [menuOpen]);

  const isLaboratorioRoute = useMemo(() => {
    const p = (location.pathname || "").toLowerCase();
    if (p.startsWith("/tecnicos&laboratorio")) return true;
    if (p.includes("/laboratorio")) return true;
    return false;
  }, [location.pathname]);

  const doLogout = async () => {
    if (loggingOut) return;

    setLoggingOut(true);
    setMenuOpen(false);
    setCompanyMenuOpen(false);

    try {
      try {
        await fetch("https://api.logvi-havox.com/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {}

      try {
        localStorage.removeItem("access_token");
        sessionStorage.clear();
      } catch {}

      await logout();

      window.location.replace("/login");
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSelectCompany = async (slug: string) => {
    if (!slug) return;
    const dep = (sessao?.departamento || "").toLowerCase();
    if (dep !== "desenvolvedor") return;
    if (changingCompanySlug) return;

    setChangingCompanySlug(slug);

    try {
      let token = "";
      try {
        if (typeof window !== "undefined") {
          token = localStorage.getItem("access_token") || "";
        }
      } catch {
        token = "";
      }

      const res = await fetch(
        "https://api.eva-googlemarine.com/geral/alterar-empresa",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ slug }),
        }
      );

      if (!res.ok) {
        let extra = "";
        try {
          extra = await res.text();
        } catch {}
        console.error(
          "Erro ao alterar empresa:",
          res.status,
          res.statusText,
          extra
        );
        window.alert(
          "Não foi possível alterar a empresa. Verifique o slug ou tente novamente."
        );
        return;
      }

      setMenuOpen(false);
      setCompanyMenuOpen(false);
      window.location.reload();
    } catch (e) {
      console.error("Falha na requisição alterar-empresa:", e);
      window.alert(
        "Falha de rede ao alterar a empresa. Verifique sua conexão e tente novamente."
      );
    } finally {
      setChangingCompanySlug(null);
    }
  };

  const profileUrl = useMemo(() => {
    if (liveUrl) return liveUrl;
    const local = resolveProfileUrl(pictureId, nome);
    return local || undefined;
  }, [liveUrl, pictureId, nome]);

  const hasName = !!nome;

  const dep = (departamento || "").toLowerCase();
  const canShowFX =
    dep === "diretoria" ||
    dep === "comercial" ||
    dep === "coordenacao" ||
    dep === "desenvolvedor";
  const isRH = dep === "recursos-humanos";
  const isDev = dep === "desenvolvedor";

  const handleGoApiDoc = () => {
    navigate("/apidoc");
  };

  const headerContainerStyle: React.CSSProperties = useMemo(
    () => ({
      ...styles.container,
      background: `linear-gradient(270deg, ${"#094350"} 1.00%, ${"#062128"} 99.00%)`,
    }),
    [theme]
  );

  const handleToggleMenu = () => {
    setMenuOpen((v) => {
      const next = !v;
      if (!next) setCompanyMenuOpen(false);
      return next;
    });
  };

  return (
    <motion.header
      style={headerContainerStyle}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={{
          display: "flex",
          alignItems: "center",
          marginRight: 18,
          flexShrink: 1,
          minWidth: "max-content",
        }}
      >
        <div
          onClick={() => navigate("/acess/home")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/acess/home");
            }
          }}
          style={styles.systemInfo}
        >
          <img
            src={logviComplete}
            alt="Logvi"
            style={styles.systemLogo}
            draggable={false}
          />

          <span style={styles.systemLogoDivider}>|</span>

          <span style={styles.systemTitle}>{systemTitle}</span>

          <span style={styles.systemLogoDivider}>|</span>

          <span style={styles.systemCompany}>
            {empresaRazaoSocial} {empresaCnpj}
          </span>
        </div>
      </motion.div>

      <div style={styles.userInfo} ref={menuRef}>
        <AnimatePresence>
          {false && isDev && !isLaboratorioRoute && (
            <>
              <motion.button
                key="dev-api-doc-badge"
                type="button"
                style={styles.devApiBadge}
                onClick={handleGoApiDoc}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
              >
                <span
                  style={{
                    fontFamily: '"Nunito", "Inter", sans-serif',
                    marginRight: 4,
                  }}
                >
                  {"</>"}
                </span>
                <span>API DOC</span>
              </motion.button>

              <motion.span
                key="dev-badge"
                style={styles.devBadge}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
              >
                MODO DESENVOLVEDOR
              </motion.span>
            </>
          )}
        </AnimatePresence>

        <motion.div
          style={{
            ...styles.userTrigger,
            ...(userAreaHovered ? styles.userTriggerHover : {}),
            ...(menuOpen ? styles.userTriggerOpen : {}),
          }}
          onMouseEnter={() => setUserAreaHovered(true)}
          onMouseLeave={() => setUserAreaHovered(false)}
          onClick={handleToggleMenu}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggleMenu();
            }
          }}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          whileHover={{ y: -0.5 }}
          whileTap={{ scale: 0.99 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={nome || "nome-loading"}
              style={{
                ...styles.name,
                color: userAreaHovered || menuOpen ? "#8B93A6" : "#FFFFFF",
              }}
              initial={{ opacity: 0, x: 4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -4 }}
              transition={{ duration: 0.2 }}
            >
              {nome || "—"}
            </motion.span>
          </AnimatePresence>

          <AnimatePresence initial={false} mode="wait">
            {profileUrl ? (
              <motion.img
                key="avatar-img"
                src={profileUrl}
                alt={nome ? `Foto de ${nome}` : "Foto do usuário"}
                style={styles.avatar}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
                loading="lazy"
              />
            ) : (
              <motion.div
                key="avatar-fallback"
                style={{ ...styles.avatar, ...styles.avatarFallback }}
                initial={{ scale: 0.96, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.96, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 22 }}
              >
                {hasName ? getInitials(nome) : ""}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              {isDev && (
                <motion.button
                  type="button"
                  style={styles.menuItem}
                  onClick={() => setCompanyMenuOpen((prev) => !prev)}
                  whileHover={{ backgroundColor: "rgba(102,238,248,0.08)" }}
                  whileTap={{ scale: 0.995 }}
                >
                  <span style={{ marginRight: 8 }}>←</span>
                  Selecionar empresa
                </motion.button>
              )}

              <motion.button
                type="button"
                style={styles.menuItem}
                onClick={doLogout}
                disabled={loggingOut}
                whileHover={{ backgroundColor: "rgba(102,238,248,0.08)" }}
                whileTap={{ scale: 0.995 }}
              >
                {loggingOut ? "Saindo..." : "Sair"}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    height: "56px",
    minHeight: "56px",
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0.8rem 1.5rem",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    boxSizing: "border-box",
    transition: "all 0.3s ease",
    background: "linear-gradient(270deg, #27330A 37.72%, #1f2908 82.21%)",
    border: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
    overflow: "visible",
  },

  systemInfo: {
    margin: 0,
    marginLeft: -8,
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none",
  },

  systemLogo: {
    height: 18,
    width: "auto",
    display: "block",
    flexShrink: 0,
    userSelect: "none",
    pointerEvents: "none",
  },

  systemTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#FFFFFF",
    lineHeight: 1,
    flexShrink: 0,
    textTransform: "uppercase",
  },

  systemDivider: {
    opacity: 0.65,
    color: "#FFFFFF",
    lineHeight: 1,
    flexShrink: 0,
  },

  systemCompany: {
    fontSize: 12,
    fontWeight: 700,
    color: "#FFFFFF",
    textTransform: "uppercase",
    lineHeight: 1,
    flexShrink: 0,
    overflow: "visible",
    textOverflow: "clip",
  },

  topHairline: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background: "rgba(255,255,255,0.18)",
    pointerEvents: "none",
  },

  userInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    position: "relative",
    minWidth: 0,
    flexShrink: 0,
  },

  userTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 36,
    padding: "0.5px 9px",
    borderRadius: 999,
    cursor: "pointer",
    userSelect: "none",
    transition:
      "background 0.22s ease, box-shadow 0.22s ease, transform 0.18s ease",
    background: "transparent",
    border: "1px solid transparent",
  },

  userTriggerHover: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(246,247,249,0.94) 100%)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.85), 0 2px 8px rgba(0,0,0,0.10)",
    border: "1px solid rgba(255,255,255,0.55)",
  },

  userTriggerOpen: {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,247,249,0.96) 100%)",
    boxShadow:
      "inset 0 1px 0 rgba(255,255,255,0.92), 0 3px 10px rgba(0,0,0,0.12)",
    border: "1px solid rgba(255,255,255,0.62)",
  },

  devApiBadge: {
    height: 28,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid rgba(102,238,248,0.35)",
    background: "rgba(102,238,248,0.10)",
    color: "#D9FBFE",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.04em",
    cursor: "pointer",
    whiteSpace: "nowrap",
    backdropFilter: "blur(4px)",
  },

  devBadge: {
    height: 28,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(255,255,255,0.08)",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },

  name: {
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: "nowrap",
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "color 0.2s ease",
  },

  avatar: {
    width: 24,
    height: 24,
    minWidth: 24,
    minHeight: 24,
    borderRadius: "50%",
    objectFit: "cover",
    userSelect: "none",
    background: "#0B5268",
    flexShrink: 0,
  },

  avatarFallback: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  menu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    minWidth: 220,
    borderRadius: 12,
    background: "#FFFFFF",
    border: "1px solid rgba(0,30,39,0.08)",
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
    overflow: "hidden",
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
  },

  menuItem: {
    width: "100%",
    minHeight: 42,
    padding: "10px 14px",
    border: "none",
    background: "#FFFFFF",
    color: "#193A17",
    textAlign: "left",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    borderBottom: "1px solid rgba(0,30,39,0.06)",
  },

  companyMenu: {
    display: "flex",
    flexDirection: "column",
    background: "#F8FBFC",
    padding: 8,
    gap: 6,
  },

  companyMenuTitle: {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#4A6470",
    padding: "2px 6px 6px 6px",
  },

  companyMenuItem: {
    width: "100%",
    minHeight: 36,
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid rgba(0,130,140,0.12)",
    background: "#FFFFFF",
    color: "#17313A",
    textAlign: "left",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default Header;
